import React, { useCallback, useEffect, useReducer, useMemo, useState } from 'react';
import { UserContext } from '../context/user-context';

const initialState = {
  user: null,
  userToken: null,
  isUserLoading: true,
  userTags: {},  // Add userTags to the initial state
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'RESTORE_STATE':
      return {
        ...state,
        ...action.payload,
        isUserLoading: false,
      };
    case 'SET_USER':
      return {
        ...state,
        user: action.payload,
      };
    case 'SET_USER_TOKEN':
      return {
        ...state,
        userToken: action.payload,
      };
    case 'SET_USER_TAGS':
      return {
        ...state,
        userTags: action.payload,
      };
    case 'RESET_STATE':
      return initialState;
    default:
      return state;
  }
};

const UserProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const bootstrapAsync = async () => {
      try {
        const user = localStorage.getItem(process.env.REACT_APP_USER_LOCALSTOAGE_KEY);
        const userToken = localStorage.getItem(process.env.REACT_APP_AUTH_TOKEN_LOCALSTOAGE_KEY);
        const userTags = JSON.parse(localStorage.getItem(process.env.REACT_APP_USER_TAGS_LOCALSTOAGE_KEY)) || {}; // Load userTags

        dispatch({
          type: 'RESTORE_STATE',
          payload: {
            user: user ? JSON.parse(user) : null,
            userToken,
            userTags,  // Restore userTags
          },
        });
      } catch (e) {
        console.error('Failed to load user state', e);
      }
    };

    bootstrapAsync();
  }, []);

  // Handlers for setting values
  const setUser = useCallback(async (user) => {
    dispatch({ type: 'SET_USER', payload: user });
    localStorage.setItem(process.env.REACT_APP_USER_LOCALSTOAGE_KEY, JSON.stringify(user));
  }, []);

  const setUserToken = useCallback(async (token) => {
    dispatch({ type: 'SET_USER_TOKEN', payload: token });
    localStorage.setItem(process.env.REACT_APP_AUTH_TOKEN_LOCALSTOAGE_KEY, token);
  }, []);

  const setUserTags = useCallback(async (tags) => {
    dispatch({ type: 'SET_USER_TAGS', payload: tags });
    localStorage.setItem(process.env.REACT_APP_USER_TAGS_LOCALSTOAGE_KEY, JSON.stringify(tags)); // Store tags in localStorage
  }, []);

  const resetState = useCallback(async () => {
    dispatch({ type: 'RESET_STATE' });
    localStorage.removeItem(process.env.REACT_APP_USER_LOCALSTOAGE_KEY);
    localStorage.removeItem(process.env.REACT_APP_AUTH_TOKEN_LOCALSTOAGE_KEY);
    localStorage.removeItem(process.env.REACT_APP_USER_TAGS_LOCALSTOAGE_KEY); // Remove userTags from localStorage
  }, []);

  // Memoized context value
  const contextValue = useMemo(
    () => ({
      ...state,
      setUser,
      setUserToken,
      setUserTags,  // Add setUserTags to the context
      resetState,
    }),
    [state, setUser, setUserToken, setUserTags, resetState]
  );

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

export default UserProvider;
